/**
 * Currency and number formatting utilities for Polish locale.
 *
 * Format: "1 234,56 PLN" (space as thousands separator, comma as decimal)
 */

/**
 * Formats a number as currency with the specified symbol.
 *
 * @param value - The value to format (number, string, null, or undefined)
 * @param currency - Currency code (PLN, EUR, USD, etc.). Defaults to PLN.
 * @returns Formatted currency string (e.g., "1 234,56 PLN") or empty string for null/undefined
 *
 * @example
 * formatCurrency(1234.56)           // "1 234,56 PLN"
 * formatCurrency(1234.56, "EUR")    // "1 234,56 EUR"
 * formatCurrency(1234.56, "USD")    // "1 234,56 USD"
 * formatCurrency(null)              // ""
 * formatCurrency(0)                 // "0,00 PLN"
 */
export function formatCurrency(
  value: number | string | null | undefined,
  currency: string = "PLN"
): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return "";
  }

  // Format with Polish locale (space as thousands separator, comma as decimal)
  const formatted = numValue.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${formatted} ${currency || "PLN"}`;
}

/**
 * Parses a Polish-style currency or number string to a number.
 * Handles both Polish format (1 234,56) and standard format (1234.56).
 *
 * @param value - The string to parse
 * @returns Parsed number or null if invalid
 *
 * @example
 * parseCurrency("1 234,56 PLN")  // 1234.56
 * parseCurrency("1234.56")       // 1234.56
 * parseCurrency("1 234,56")      // 1234.56
 * parseCurrency("")              // null
 * parseCurrency("abc")           // null
 */
export function parseCurrency(value: string): number | null {
  if (!value || !value.trim()) {
    return null;
  }

  // Remove currency symbol, spaces, and other non-numeric characters except comma, dot, and minus
  const cleaned = value
    .replace(/PLN/gi, "")
    .replace(/[^\d,.\-]/g, "")
    .trim();

  if (!cleaned) {
    return null;
  }

  // Handle Polish format: comma as decimal separator
  // If there's a comma, treat it as decimal separator
  // If there's both comma and dot, the last one is the decimal separator
  let normalized = cleaned;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");

  if (lastComma > lastDot) {
    // Polish format: comma is decimal separator
    // Remove all dots (thousands separators) and replace comma with dot
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    // Standard format: dot is decimal separator
    // Remove all commas (thousands separators)
    normalized = cleaned.replace(/,/g, "");
  }

  const num = parseFloat(normalized);
  return isNaN(num) ? null : num;
}

/**
 * Formats a number with Polish locale (without currency symbol).
 * Useful for displaying numbers that aren't currency but should follow locale conventions.
 *
 * @param value - The value to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 */
export function formatNumber(
  value: number | string | null | undefined,
  decimals: number = 2
): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return "";
  }

  return numValue.toLocaleString("pl-PL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formats a date string for display in Polish format (DD.MM.YYYY).
 * Accepts ISO format (YYYY-MM-DD) or Polish format (DD.MM.YYYY).
 *
 * @param value - Date string to format
 * @returns Formatted date string or "-" for empty values
 */
/**
 * Formats a number as compact currency with M/k suffixes.
 * Designed for KPI cards and dashboards where space is limited.
 *
 * @param value - The value to format (number, string, null, or undefined)
 * @param currency - Currency code (PLN, EUR, USD, etc.). Defaults to PLN.
 * @returns Compact currency string (e.g., "24,6M PLN", "235k PLN") or empty string for null/undefined
 *
 * @example
 * formatCompactCurrency(24602345.67)  // "24,6M PLN"
 * formatCompactCurrency(1234567.89)   // "1,2M PLN"
 * formatCompactCurrency(234567.89)    // "235k PLN"
 * formatCompactCurrency(12345.67)     // "12,3k PLN"
 * formatCompactCurrency(5432.10)      // "5 432,10 PLN" (unchanged)
 * formatCompactCurrency(null)         // ""
 */
export function formatCompactCurrency(
  value: number | string | null | undefined,
  currency: string = "PLN"
): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return "";
  }

  const absValue = Math.abs(numValue);
  const sign = numValue < 0 ? "-" : "";

  if (absValue >= 1_000_000) {
    return `${sign}${(absValue / 1_000_000).toLocaleString("pl-PL", { maximumFractionDigits: 1 })}M ${currency}`;
  }

  if (absValue >= 10_000) {
    return `${sign}${Math.round(absValue / 1000)}k ${currency}`;
  }

  return formatCurrency(value, currency);
}

export function formatDateForDisplay(value: string | null | undefined): string {
  if (!value || value === "null" || value === "") return "-";

  // Try ISO format (YYYY-MM-DD)
  if (value.includes("-")) {
    const parts = value.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}.${month}.${year}`;
    }
  }

  // Already in DD.MM.YYYY format or other - return as-is
  return value;
}
